/**
 * Performance Monitor Toggle
 * Development tool for toggling performance dashboard visibility
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PerformanceDashboard from './PerformanceDashboard';

interface PerformanceMonitorToggleProps {
  enabled?: boolean;
  hotkey?: string;
}

const PerformanceMonitorToggle: React.FC<PerformanceMonitorToggleProps> = ({
  enabled = process.env.NODE_ENV === 'development',
  hotkey = 'KeyP'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHotkeyPressed, setIsHotkeyPressed] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Toggle with Ctrl/Cmd + Shift + P
      if (event.code === hotkey && event.ctrlKey && event.shiftKey) {
        event.preventDefault();
        setIsVisible(prev => !prev);
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === hotkey && event.ctrlKey && event.shiftKey) {
        setIsHotkeyPressed(true);
        setTimeout(() => setIsHotkeyPressed(false), 200);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keypress', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [enabled, hotkey]);

  if (!enabled) return null;

  return (
    <>
      {/* Floating toggle button */}
      <motion.button
        onClick={() => setIsVisible(prev => !prev)}
        className={`fixed bottom-4 right-4 z-40 w-12 h-12 rounded-full shadow-lg transition-all duration-200 ${
          isVisible 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
        }`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: isHotkeyPressed ? 1.2 : 1,
          backgroundColor: isHotkeyPressed ? '#3B82F6' : undefined
        }}
        title="Toggle Performance Monitor (Ctrl+Shift+P)"
      >
        <svg
          className="w-6 h-6 mx-auto"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      </motion.button>

      {/* Performance Dashboard */}
      <PerformanceDashboard
        isVisible={isVisible}
        onClose={() => setIsVisible(false)}
        position="top-right"
      />

      {/* Hotkey indicator */}
      {isHotkeyPressed && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed bottom-20 right-4 z-50 bg-blue-600 text-white px-3 py-1 rounded text-sm"
        >
          Performance Monitor Toggled
        </motion.div>
      )}
    </>
  );
};

export default PerformanceMonitorToggle;